/**
 * SME-Borrower permission model (EM-307, collapsed onto the server definition by EM-937 / F6).
 *
 * ## Why this file no longer contains the matrix
 *
 * It used to declare a 22-value permission union and a hard-coded €25,000 ceiling, mirroring the
 * backend `service.business.authz.Permission` enum by hand. It had already drifted: the backend
 * held 23 constants including `CHANGE_PIN`, which the portal simply did not know about, so a CFO
 * silently lost an action they were entitled to.
 *
 * FR-4.6 requires ONE authoritative role→permission definition used by both the backend mapping
 * and the portal display. A TypeScript file only stops being a second copy when it stops
 * containing the data — so the data now arrives over REST:
 *
 * - the **definition** (all roles × all actions) from `GET /api/authz/role-matrix`, via
 *   `RoleMatrixService`;
 * - the **instance** (this rep's effective union + ceiling) from `GET /me/session`, via
 *   `SME_SESSION`.
 *
 * What remains here are the *types*, which carry no policy.
 */

/**
 * A permission name, e.g. `INITIATE_PAYMENT`. Server-defined: the set of valid values is whatever
 * the backend `Permission` enum currently holds, published by `GET /api/authz/role-matrix`.
 *
 * Deliberately `string` rather than a hand-maintained union — a union here would be a second copy
 * of the enum and would drift again the moment a constant is added (which is precisely what
 * happened with `CHANGE_PIN`). Call sites keep their compile-time readability by passing string
 * literals; correctness is enforced by the backend, which is the sole decision-maker anyway.
 */
export type SmePermission = string;

/**
 * The authority role scope, as surfaced by `GET /me/session`.
 *
 * **Canonical casing is UPPERCASE**, matching the backend `RepresentativeRole` enum. Before
 * EM-937 two vocabularies coexisted — a lowercase wire form (`'owner' | 'cfo' | 'accountant'`)
 * and this uppercase one — bridged by ad-hoc `.toLowerCase()` calls scattered across components.
 * Under multi-role that is not cosmetic: comparing a role *array* with mismatched casing yields an
 * empty union and the rep sees nothing. Normalise once, at the REST-service boundary
 * (`normaliseRole` below), and never again downstream.
 */
export type SmeRoleScope = 'OWNER' | 'CFO' | 'ACCOUNTANT' | 'MEMBER' | string;

/**
 * Normalises a role value from any source to the canonical uppercase form.
 *
 * The ONE place casing is reconciled (Rule 17). Use it wherever a role crosses the REST boundary
 * or comes from a form control; do not add a second `.toLowerCase()` / `.toUpperCase()` anywhere.
 */
export function normaliseRole(role: string | null | undefined): SmeRoleScope | null {
  const trimmed = (role ?? '').trim();
  return trimmed.length > 0 ? trimmed.toUpperCase() : null;
}

/** Normalises a role list to canonical uppercase, dropping blanks. */
export function normaliseRoles(roles: readonly (string | null | undefined)[] | null | undefined): SmeRoleScope[] {
  return (roles ?? []).map(normaliseRole).filter((r): r is SmeRoleScope => r !== null);
}
